/**
 * TechSprint Demo — public cinematic storyboard.
 *
 * A 3-minute auto-playing walkthrough of the Altara Consumer Intelligence
 * platform, told through Nomsa's journey. Two acts:
 *   • Consumer side — how Altara helps her make a better financial decision
 *     AND averts an impersonation-AI scam.
 *   • Institution side — the intelligence this generates for ABC Wealth
 *     (conduct / TCF outcomes) and AI oversight (explainable evidence chain).
 *
 * Designed for a single-take 1920x1080 screen recording. No wallet shell,
 * no sidebar — a clean surface. All data is deterministic mock so playback
 * is smooth and reproducible.
 *
 * Controls:  Space = play/pause · ← → = prev/next · R = restart.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, Pause, SkipBack, SkipForward, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { SCENES } from "@/components/techsprint/scenes";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const TOTAL_MS = SCENES.reduce((s, sc) => s + sc.durationMs, 0);

export default function TechSprintDemoPage() {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [sceneElapsed, setSceneElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true); // silent by default — audio wired later
  const lastFrameRef = useRef(performance.now());
  const rafRef = useRef(null);

  const scene = SCENES[sceneIdx];
  const sceneProgress = Math.min(1, sceneElapsed / scene.durationMs);

  const goTo = useCallback((idx) => {
    const clamped = Math.max(0, Math.min(SCENES.length - 1, idx));
    setSceneIdx(clamped);
    setSceneElapsed(0);
    lastFrameRef.current = performance.now();
  }, []);

  const restart = useCallback(() => {
    goTo(0);
    setPlaying(true);
  }, [goTo]);

  // rAF loop
  useEffect(() => {
    if (!playing) return;
    const tick = () => {
      const now = performance.now();
      const dt = now - lastFrameRef.current;
      lastFrameRef.current = now;
      setSceneElapsed(prev => {
        const next = prev + dt;
        if (next >= scene.durationMs) {
          if (sceneIdx < SCENES.length - 1) {
            setSceneIdx(sceneIdx + 1);
            return 0;
          }
          setPlaying(false);
          return scene.durationMs;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, scene.durationMs, sceneIdx]);

  // Keyboard controls
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space") { e.preventDefault(); setPlaying(p => !p); }
      if (e.code === "ArrowRight") { goTo(sceneIdx + 1); }
      if (e.code === "ArrowLeft")  { goTo(sceneIdx - 1); }
      if (e.key.toLowerCase() === "r") { restart(); }
      if (e.key.toLowerCase() === "m") { setMuted(m => !m); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sceneIdx, goTo, restart]);

  const totalElapsed = useMemo(() => {
    let sum = sceneElapsed;
    for (let i = 0; i < sceneIdx; i++) sum += SCENES[i].durationMs;
    return sum;
  }, [sceneIdx, sceneElapsed]);
  const totalPct = Math.min(100, (totalElapsed / TOTAL_MS) * 100);

  const SceneComponent = scene.Component;

  return (
    <div
      className="fixed inset-0 overflow-hidden font-sans"
      style={{ background: NAVY, color: "white" }}
      data-testid="techsprint-demo-page"
    >
      {/* Cinematic aspect frame — 16:9, scales to viewport */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="relative"
          style={{
            width: "min(100vw, 177.78vh)",
            aspectRatio: "16 / 9",
            background: NAVY,
            overflow: "hidden",
          }}
          data-testid="techsprint-stage"
        >
          {/* Subtle background grain */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.06]"
               style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #C9A24A 0, transparent 55%), radial-gradient(circle at 80% 70%, #6B8CFF 0, transparent 55%)" }} />

          {/* Top bar — brand + scene counter */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-8 py-4 z-30">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                   style={{ background: GOLD }}>
                <span className="font-display font-bold text-lg" style={{ color: NAVY }}>A</span>
              </div>
              <div>
                <div className="text-[9px] tracking-[0.28em] uppercase" style={{ color: GOLD }}>Altara</div>
                <div className="text-[13px] font-semibold leading-tight">Consumer Intelligence · TechSprint</div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-[11px] uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.55)" }}>
              <span data-testid="scene-counter">Scene {sceneIdx + 1} · {SCENES.length}</span>
              <span data-testid="scene-clock">
                {formatMs(totalElapsed)} / {formatMs(TOTAL_MS)}
              </span>
            </div>
          </div>

          {/* Scene canvas */}
          <div className="absolute inset-0 flex items-center justify-center pt-16 pb-32 px-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={sceneIdx}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="w-full h-full flex items-center justify-center"
                data-testid={`scene-${sceneIdx}`}
              >
                <SceneComponent progress={sceneProgress} />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Caption bar */}
          <div className="absolute left-0 right-0 bottom-24 px-12 z-20 pointer-events-none">
            <AnimatePresence mode="wait">
              <motion.div
                key={sceneIdx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="max-w-4xl mx-auto text-center"
                data-testid="scene-caption"
              >
                <div className="text-[10px] tracking-[0.34em] uppercase mb-2"
                     style={{ color: GOLD }}>{scene.act}</div>
                <div className="text-[22px] leading-snug font-medium" style={{ color: "rgba(255,255,255,0.94)" }}>
                  {scene.caption}
                </div>
                {scene.subCaption && (
                  <div className="text-[13px] mt-2" style={{ color: "rgba(255,255,255,0.55)" }}>
                    {scene.subCaption}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Bottom controls + progress */}
          <div className="absolute left-0 right-0 bottom-0 z-30" data-testid="techsprint-controls">
            {/* Progress bar */}
            <div className="h-1 bg-white/10 relative">
              <div
                className="absolute top-0 left-0 h-full transition-[width] duration-100"
                style={{ width: `${totalPct}%`, background: GOLD }}
                data-testid="progress-bar"
              />
              {/* Scene tick marks */}
              {SCENES.reduce((acc, sc, i) => {
                const prevSum = acc.sum;
                acc.marks.push(prevSum);
                acc.sum = prevSum + sc.durationMs;
                return acc;
              }, { marks: [], sum: 0 }).marks.slice(1).map((m, i) => (
                <div key={i} className="absolute top-0 h-full w-px bg-white/20"
                     style={{ left: `${(m / TOTAL_MS) * 100}%` }} />
              ))}
            </div>
            <div className="px-8 py-3 flex items-center gap-3 bg-black/30 backdrop-blur">
              <button
                onClick={() => setPlaying(p => !p)}
                data-testid="control-play-pause"
                className="w-10 h-10 rounded-full flex items-center justify-center transition"
                style={{ background: GOLD, color: NAVY }}
              >
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </button>
              <button
                onClick={() => goTo(sceneIdx - 1)}
                data-testid="control-prev"
                className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition"
              ><SkipBack className="w-4 h-4" /></button>
              <button
                onClick={() => goTo(sceneIdx + 1)}
                data-testid="control-next"
                className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition"
              ><SkipForward className="w-4 h-4" /></button>
              <button
                onClick={restart}
                data-testid="control-restart"
                className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition"
              ><RotateCcw className="w-4 h-4" /></button>

              <div className="ml-auto flex items-center gap-3 text-[11px] uppercase tracking-widest text-white/50">
                <span className="hidden lg:inline">Space play/pause · ← → skip · R restart</span>
                <button
                  onClick={() => setMuted(m => !m)}
                  data-testid="control-mute"
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white transition"
                  title={muted ? "Audio not wired — captions only" : "Audio muted"}
                >
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatMs(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
